/**
 * ExperimentDetailPanel
 *
 * A standalone, data-driven panel that renders experiment details.
 * Supports three variants: suggestion, processing, and analytics.
 * Used by both ExperimentDetailModal (as inner content) and the
 * experiments behaviour slot (inline in chat).
 *
 * All data comes from props — no hardcoded mock data.
 */
import React from 'react';
import { cn, Badge, type BadgeProps } from '@strivelabs/ui-core';
import { Button } from '@strivelabs/ui-core/v2';

import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Target,
  DollarSign,
  Users,
  MousePointerClick,
  Sparkles,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  Activity,
} from 'lucide-react';
import { LineChart } from '../charts/LineChart';
import { BarChart } from '../charts/BarChart';

// ============================================================================
// Types
// ============================================================================

export type ExperimentPerformanceMetric = {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  color_theme?: 'green' | 'blue' | 'purple' | 'orange' | 'red';
};

export type ExperimentIssue = {
  title: string;
  severity: string;
  text: string;
};

export type ExperimentChartConfig = {
  data: Record<string, string | number>[];
  x_axis_key: string;
  lines?: Array<{ data_key: string; color: string; y_axis_id?: 'left' | 'right' }>;
  data_keys?: Array<{ key: string; color: string; name: string }>;
  config?: Record<string, { label: string; color: string }>;
};

export type ExperimentDetailedMetricRow = {
  metric: string;
  control: string;
  current: string;
  change: string;
  trend: 'up' | 'down';
};

export type ExperimentAiInsight = {
  title: string;
  text: string;
};

export type ExperimentFollowUp = {
  title: string;
  description: string;
  impact: string;
  meta: string;
};

export type ExperimentExpectedResult = {
  title: string;
  text: string;
  color_theme: 'green' | 'blue' | 'purple';
};

export type ExperimentSlotData = {
  title: string;
  description: string;
  variant: 'suggestion' | 'processing' | 'analytics';
  experiment_id?: number;
  channel?: string;
  start_date?: string;
  end_date?: string;
  status?: 'great' | 'underperforming' | 'neutral';
  performance_overview?: ExperimentPerformanceMetric[];
  issues?: ExperimentIssue[];
  performance_trend?: ExperimentChartConfig;
  variant_comparison?: ExperimentChartConfig;
  detailed_metrics?: ExperimentDetailedMetricRow[];
  ai_insights?: ExperimentAiInsight[];
  follow_up_experiments?: ExperimentFollowUp[];
  expected_results?: ExperimentExpectedResult[];
  /** Explanation text for the "What This Experiment Will Do" section (suggestion variant) */
  what_it_does?: string;
  /** Badge variant for the channel badge (from @strivelabs/ui-core Badge) */
  channel_badge_variant?: BadgeProps['variant'];
};

export interface ExperimentDetailPanelProps {
  /** Experiment data to render */
  data: ExperimentSlotData;
  /** When true, uses compact layout (reduced chart heights) for inline slot rendering */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Action callbacks — frontend-only, not sent to chat backend */
  onPause?: () => void;
  onStop?: () => void;
  onStart?: () => void;
  onDismiss?: () => void;
  onApplyRecommendations?: () => void;
  onExtendDuration?: () => void;
  /** When true, action buttons are disabled (for historical/read-only messages) */
  readOnly?: boolean;
}

// ============================================================================
// Color theme helpers
// ============================================================================

const METRIC_THEME_CLASSES: Record<string, { bg: string; border: string; text: string; value: string; change: string }> = {
  green: { bg: 'from-green-50 to-emerald-50', border: 'border-green-100', text: 'text-green-700', value: 'text-green-900', change: 'text-green-600' },
  blue: { bg: 'from-blue-50 to-cyan-50', border: 'border-blue-100', text: 'text-blue-700', value: 'text-blue-900', change: 'text-blue-600' },
  purple: { bg: 'from-purple-50 to-violet-50', border: 'border-purple-100', text: 'text-purple-700', value: 'text-purple-900', change: 'text-purple-600' },
  orange: { bg: 'from-orange-50 to-amber-50', border: 'border-orange-100', text: 'text-orange-700', value: 'text-orange-900', change: 'text-orange-600' },
  red: { bg: 'from-red-50 to-rose-50', border: 'border-red-100', text: 'text-red-700', value: 'text-red-900', change: 'text-red-600' },
};

const METRIC_ICONS: Record<string, React.ReactNode> = {
  green: <TrendingUp className="w-4 h-4 text-green-600" />,
  blue: <DollarSign className="w-4 h-4 text-blue-600" />,
  purple: <MousePointerClick className="w-4 h-4 text-purple-600" />,
  orange: <Users className="w-4 h-4 text-orange-600" />,
  red: <TrendingDown className="w-4 h-4 text-red-600" />,
};

const EXPECTED_RESULT_THEME: Record<string, { bg: string; border: string; iconBg: string; title: string; text: string; icon: React.ReactNode }> = {
  green: {
    bg: 'from-green-50 to-emerald-50', border: 'border-green-100', iconBg: 'bg-green-100',
    title: 'text-green-900', text: 'text-green-700', icon: <TrendingUp className="w-4 h-4 text-green-600" />,
  },
  blue: {
    bg: 'from-blue-50 to-cyan-50', border: 'border-blue-100', iconBg: 'bg-blue-100',
    title: 'text-blue-900', text: 'text-blue-700', icon: <Target className="w-4 h-4 text-blue-600" />,
  },
  purple: {
    bg: 'from-purple-50 to-violet-50', border: 'border-purple-100', iconBg: 'bg-purple-100',
    title: 'text-purple-900', text: 'text-purple-700', icon: <DollarSign className="w-4 h-4 text-purple-600" />,
  },
};

// ============================================================================
// Sub-components
// ============================================================================

function AiInsightItem({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex items-start gap-2 bg-white/60 rounded-lg p-3">
      <Lightbulb className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-medium text-[#181818] mb-1">{title}</p>
        <p className="text-xs text-[#6b6b6b]">{text}</p>
      </div>
    </div>
  );
}

function FollowUpCard({
  title,
  description,
  impact,
  meta,
}: {
  title: string;
  description: string;
  impact: string;
  meta: string;
}) {
  return (
    <div className="bg-white rounded-lg p-4 border border-blue-100 hover:border-blue-300 transition-colors cursor-pointer">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-medium text-[#181818] text-sm mb-1">{title}</h4>
          <p className="text-xs text-[#6b6b6b] mb-2">{description}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-blue-600 shrink-0 mt-1" />
      </div>
      <div className="flex gap-3 text-xs">
        <span className="text-green-700 font-medium">{impact}</span>
        <span className="text-[#6b6b6b]">{meta}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export const ExperimentDetailPanel: React.FC<ExperimentDetailPanelProps> = ({
  data,
  compact = false,
  className,
  onPause,
  onStop,
  onStart,
  onDismiss,
  onApplyRecommendations,
  onExtendDuration,
  readOnly = false,
}) => {
  const {
    title,
    description,
    variant,
    channel,
    start_date,
    end_date,
    status,
    performance_overview,
    issues,
    performance_trend,
    variant_comparison,
    detailed_metrics,
    ai_insights,
    follow_up_experiments,
    expected_results,
    what_it_does,
    channel_badge_variant,
  } = data;

  const isSuggestion = variant === 'suggestion';
  const isProcessing = variant === 'processing';
  const isUnderperforming = status === 'underperforming';
  const isHighImpact = status === 'great';

  const chartHeight = compact ? 220 : 300;

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Header — same as modal but without close button */}
      <div className="bg-white border-b border-black/[0.08] p-6 shrink-0">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {isSuggestion && (
              <div className="bg-gradient-to-br from-[#F7BB5C] via-[#F56B10] to-[#B34086] p-1.5 rounded-lg shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
            )}
            <h2 className="font-medium text-[#181818] text-xl leading-snug">{title}</h2>
          </div>
          <p className="text-sm text-[#6b6b6b] leading-relaxed">{description}</p>
          {!isSuggestion && (
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              {channel && (
                <Badge variant={channel_badge_variant ?? 'outline'}>
                  {channel}
                </Badge>
              )}
              {start_date && (
                <span className="text-xs text-[#6b6b6b] flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Started {start_date}
                </span>
              )}
              {end_date && (
                <span className="text-xs text-[#6b6b6b]">→ {end_date}</span>
              )}
              {isHighImpact && (
                <Badge variant="outlineGreen">High Impact</Badge>
              )}
              {isUnderperforming && (
                <Badge variant="outlineRed">Needs Attention</Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1 min-h-0">
        {isSuggestion ? (
          /* ── Suggestion variant ── */
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-medium text-[#181818] mb-3">What This Experiment Will Do</h3>
              <div className="bg-black/[0.02] rounded-xl p-4">
                <p className="text-sm text-[#6b6b6b] leading-relaxed">
                  {what_it_does ??
                    `This experiment will test ${title.toLowerCase()} to determine the optimal approach for improving your marketing performance. The AI has identified this opportunity based on analysis of your current data patterns and industry benchmarks.`}
                </p>
              </div>
            </div>

            {expected_results && expected_results.length > 0 && (
              <div>
                <h3 className="font-medium text-[#181818] mb-3">Expected Results</h3>
                <div className="space-y-3">
                  {expected_results.map((result) => {
                    const theme = EXPECTED_RESULT_THEME[result.color_theme] || EXPECTED_RESULT_THEME.green;
                    return (
                      <div
                        key={result.title}
                        className={cn('bg-gradient-to-br border rounded-xl p-4', theme.bg, theme.border)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={cn('p-2 rounded-lg shrink-0', theme.iconBg)}>
                            {theme.icon}
                          </div>
                          <div>
                            <h4 className={cn('font-medium text-sm mb-1', theme.title)}>{result.title}</h4>
                            <p className={cn('text-xs', theme.text)}>{result.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer actions */}
            <div className="flex gap-3 pt-2 border-t border-black/[0.08]">
              <Button
                className="flex-1"
                onClick={onStart}
                disabled={readOnly || !onStart}
              >
                Start This Experiment
              </Button>
              {onDismiss && (
                <Button variant="secondary" onClick={onDismiss} disabled={readOnly}>
                  Maybe Later
                </Button>
              )}
            </div>
          </div>
        ) : isProcessing ? (
          /* ── Processing variant ── */
          <div className="p-6 space-y-6">
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="bg-blue-50 p-4 rounded-full mb-4">
                <Activity className="w-8 h-8 text-blue-600 animate-pulse" />
              </div>
              <h3 className="font-medium text-[#181818] text-lg mb-2">
                Experiment In Progress
              </h3>
              <p className="text-sm text-[#6b6b6b] max-w-md leading-relaxed">
                This experiment is currently being processed. Detailed analytics
                and performance metrics will be available once enough data has
                been collected.
              </p>
            </div>

            {/* Footer actions */}
            <div className="flex gap-3 pt-4 border-t border-black/[0.08]">
              <Button variant="secondary" onClick={onPause} disabled={readOnly || !onPause}>
                Pause Experiment
              </Button>
              <Button
                variant="secondary"
                tone="danger"
                onClick={onStop}
                disabled={readOnly || !onStop}
              >
                Stop Experiment
              </Button>
            </div>
          </div>
        ) : (
          /* ── Analytics variant ── */
          <div className="p-6 space-y-6">
            {/* Performance overview metrics */}
            {performance_overview && performance_overview.length > 0 && (
              <div>
                <h3 className="font-medium text-[#181818] mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Performance Overview
                </h3>
                <div className={cn('grid gap-4', compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4')}>
                  {performance_overview.map((metric) => {
                    const themeKey = metric.color_theme || (metric.trend === 'up' ? 'green' : 'red');
                    const theme = METRIC_THEME_CLASSES[themeKey];
                    return (
                      <div
                        key={metric.label}
                        className={cn('rounded-xl p-4 border bg-gradient-to-br', theme.bg, theme.border)}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {METRIC_ICONS[themeKey]}
                          <span className={cn('text-xs font-medium', theme.text)}>{metric.label}</span>
                        </div>
                        <div className={cn('text-2xl font-semibold', theme.value)}>{metric.value}</div>
                        <div className={cn('text-xs mt-1 flex items-center gap-1', theme.change)}>
                          {metric.trend === 'up' ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          {metric.change}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* What&apos;s Going Wrong — underperforming only */}
            {isUnderperforming && issues && issues.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-red-100 p-2 rounded-lg shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-red-900 mb-1">What&apos;s Going Wrong</h3>
                    <p className="text-sm text-red-700">Key issues identified in this experiment</p>
                  </div>
                </div>
                <div className="space-y-3 ml-11">
                  {issues.map((issue) => (
                    <div key={issue.title} className="bg-white rounded-lg p-3 border border-red-100">
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-medium text-red-900 text-sm">{issue.title}</span>
                        <span className="text-xs text-red-600">{issue.severity}</span>
                      </div>
                      <p className="text-xs text-red-700">{issue.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Trend Chart */}
            {performance_trend && performance_trend.data.length > 0 && (
              <div>
                <h3 className="font-medium text-[#181818] mb-3">Performance Trend</h3>
                <div className="border border-black/[0.08] rounded-xl overflow-hidden">
                  <LineChart
                    data={performance_trend.data}
                    xAxisKey={performance_trend.x_axis_key}
                    lines={(performance_trend.lines || []).map((l) => ({
                      dataKey: l.data_key,
                      stroke: l.color,
                      strokeWidth: 2,
                      type: 'monotone' as const,
                      ...(l.y_axis_id && { yAxisId: l.y_axis_id }),
                    }))}
                    config={performance_trend.config || {}}
                    height={chartHeight}
                    showGrid
                    showTooltip
                    showLegend
                    expandable={false}
                  />
                </div>
              </div>
            )}

            {/* Variant Comparison Chart */}
            {variant_comparison && variant_comparison.data.length > 0 && (
              <div>
                <h3 className="font-medium text-[#181818] mb-3">Variant Performance Comparison</h3>
                <div className="border border-black/[0.08] rounded-xl overflow-hidden">
                  <BarChart
                    data={variant_comparison.data}
                    xAxisKey={variant_comparison.x_axis_key}
                    dataKeys={(variant_comparison.data_keys || []).map((dk) => ({
                      key: dk.key,
                      color: dk.color,
                      name: dk.name,
                    }))}
                    height={chartHeight}
                    showGrid
                    showLegend
                    showTooltip
                    expandable={false}
                  />
                </div>
              </div>
            )}

            {/* Detailed Metrics Table */}
            {detailed_metrics && detailed_metrics.length > 0 && (
              <div>
                <h3 className="font-medium text-[#181818] mb-3">Detailed Metrics Breakdown</h3>
                <div className="rounded-xl border border-black/[0.08] overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-black/[0.02]">
                      <tr>
                        {['Metric', 'Control', 'Current', 'Change'].map((h) => (
                          <th
                            key={h}
                            className="text-left text-xs font-medium text-[#6b6b6b] px-4 py-3"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/[0.06]">
                      {detailed_metrics.map((row) => (
                        <tr key={row.metric} className="hover:bg-black/[0.02] transition-colors">
                          <td className="px-4 py-3 text-sm text-[#181818] font-medium">{row.metric}</td>
                          <td className="px-4 py-3 text-sm text-[#6b6b6b]">{row.control}</td>
                          <td className="px-4 py-3 text-sm text-[#181818] font-medium">{row.current}</td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'text-sm font-medium flex items-center gap-1',
                                row.trend === 'up' ? 'text-green-600' : 'text-red-600',
                              )}
                            >
                              {row.trend === 'up' ? (
                                <TrendingUp className="w-3 h-3" />
                              ) : (
                                <TrendingDown className="w-3 h-3" />
                              )}
                              {row.change}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* AI Insights & Recommendations */}
            {ai_insights && ai_insights.length > 0 && (
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-gradient-to-br from-[#F7BB5C] via-[#F56B10] to-[#B34086] p-2 rounded-lg shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#181818] mb-1">AI Insights & Recommendations</h4>
                    <p className="text-xs text-[#6b6b6b]">
                      {isUnderperforming
                        ? 'Strategic recommendations to improve performance'
                        : 'Key insights and optimization opportunities'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 ml-11">
                  {ai_insights.map((insight) => (
                    <AiInsightItem key={insight.title} title={insight.title} text={insight.text} />
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Follow-Up Experiments */}
            {follow_up_experiments && follow_up_experiments.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg shrink-0">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-900 mb-1">Suggested Follow-Up Experiments</h3>
                    <p className="text-sm text-blue-700">
                      {isUnderperforming
                        ? 'Alternative approaches to test based on current learnings'
                        : 'Build on this success with these next experiments'}
                    </p>
                  </div>
                </div>
                <div className="space-y-3 ml-11">
                  {follow_up_experiments.map((exp) => (
                    <FollowUpCard
                      key={exp.title}
                      title={exp.title}
                      description={exp.description}
                      impact={exp.impact}
                      meta={exp.meta}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Footer action buttons */}
            <div className="flex gap-3 pt-4 border-t border-black/[0.08]">
              {isUnderperforming ? (
                <>
                  <Button
                    className="flex-1"
                    onClick={onApplyRecommendations}
                    disabled={readOnly || !onApplyRecommendations}
                  >
                    Apply Recommendations
                  </Button>
                  <Button variant="secondary" onClick={onPause} disabled={readOnly || !onPause}>
                    Pause Experiment
                  </Button>
                  <Button
                    variant="secondary"
                    tone="danger"
                    onClick={onStop}
                    disabled={readOnly || !onStop}
                  >
                    Stop Experiment
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" onClick={onExtendDuration} disabled={readOnly || !onExtendDuration}>
                    Extend Duration
                  </Button>
                  <Button
                    variant="secondary"
                    tone="danger"
                    onClick={onStop}
                    disabled={readOnly || !onStop}
                  >
                    Stop Experiment
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
